/**
 * Phase 2.5: Surgeon Attestation Panel
 * 
 * Final sign-off step. Single attestation, single signature.
 * After signing, the note is locked and immutable.
 */

import { Shield, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { DICTATION_ATTESTATION_TEXT } from '@/lib/dictation/types';
import type { ProcedureConfirmation } from '@/lib/dictation/types';
import { format } from 'date-fns';

interface SurgeonAttestationPanelProps {
  attested: boolean;
  onAttestedChange: (value: boolean) => void;
  onSign: () => void;
  isSigned: boolean;
  attestedAt: string | null;
  confirmations: ProcedureConfirmation[];
  disabled?: boolean;
}

export function SurgeonAttestationPanel({
  attested,
  onAttestedChange,
  onSign,
  isSigned,
  attestedAt,
  confirmations,
  disabled = false,
}: SurgeonAttestationPanelProps) {
  const pendingCount = confirmations.filter(c => c.status === 'pending').length;
  const hasPending = pendingCount > 0;

  if (isSigned) {
    return (
      <div className="space-y-4">
        <Alert className="border-success/30 bg-success/5">
          <Lock className="w-4 h-4 text-success" />
          <AlertTitle className="text-success">Operative Note Signed & Locked</AlertTitle>
          <AlertDescription className="text-sm">
            Signed {attestedAt && format(new Date(attestedAt), 'MMM d, yyyy h:mm a')}.
            This note is now immutable and ready for Phase 3 reconciliation.
          </AlertDescription>
        </Alert>

        <div className="bg-muted/30 border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold">Attestation Record</span>
          </div>
          <p className="text-xs text-muted-foreground italic">
            "{DICTATION_ATTESTATION_TEXT}"
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Surgeon Attestation</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Review and sign to lock the operative note. Once signed, the note cannot be modified.
        </p>
      </div>

      {hasPending && (
        <Alert className="border-warning/30 bg-warning/5">
          <AlertTriangle className="w-4 h-4 text-warning" />
          <AlertTitle className="text-warning">Pending Confirmations</AlertTitle>
          <AlertDescription className="text-xs">
            {pendingCount} procedure{pendingCount > 1 ? 's' : ''} still pending confirmation.
            Go back to confirm before signing.
          </AlertDescription>
        </Alert>
      )}

      <div className={cn(
        'border rounded-lg p-4 space-y-4',
        attested ? 'border-primary/50 bg-primary/5' : 'border-border/50'
      )}>
        <div className="flex items-start gap-3">
          <Checkbox
            id="attestation"
            checked={attested}
            onCheckedChange={(checked) => onAttestedChange(!!checked)}
            disabled={disabled || hasPending}
          />
          <label
            htmlFor="attestation"
            className="text-sm leading-relaxed cursor-pointer select-none"
          >
            {DICTATION_ATTESTATION_TEXT}
          </label>
        </div>

        <Button
          onClick={onSign}
          disabled={!attested || disabled || hasPending}
          className="w-full gap-2"
          size="lg"
        >
          <CheckCircle2 className="w-4 h-4" />
          Sign & Lock Operative Note
        </Button>
      </div>

      <div className="bg-muted/30 border rounded-lg p-3 flex items-start gap-2">
        <Lock className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          <span className="font-semibold">Immutability Notice:</span> After signing,
          the operative note, transcript, and all confirmations are permanently locked.
          No addenda or modifications are possible.
        </p>
      </div>
    </div>
  );
}
